import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useCreateInvoice } from '~/api/invoices';
import { useUser } from '~/api/user';
import { Button, Card, Illustration, Text, Title } from '~/components';
import { CompleteAccountDialog } from '~/features/account';
import { useDialog } from '~/hooks/useDialog';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';
import { isAccountCompleted } from '~/utils/user';

import { containerStyle, illustrationStyle, illustrationWrapperStyle, messageStyle } from './InvoicesEmptyState.css';

export const InvoicesEmptyState = () => {
  const { t } = useTranslation(['common', 'invoices']);
  const { data: user } = useUser();
  const createDraftInvoiceMutation = useCreateInvoice();
  const completeAccountDialog = useDialog<void, boolean>();
  const navigate = useNavigate();

  const createDraftInvoice = () => {
    createDraftInvoiceMutation.mutate(undefined, {
      onSuccess: ({ id }) => navigate(r(routes.createInvoice, { invoiceId: id })),
      onError: () => toast.danger({ description: t('common:error') }),
    });
  };

  return (
    <>
      <Card.Root className={containerStyle} variant="primary-light">
        <div className={illustrationWrapperStyle}>
          <Illustration className={illustrationStyle} name="SlotMachine" strokeWidth={1.4} />
        </div>

        <div className={messageStyle}>
          <Title as="h2">{t('invoices:overview.emptyState.title')}</Title>
          <Text>{t('invoices:overview.emptyState.text')}</Text>
        </div>

        {isAccountCompleted(user) ? (
          <Button icon="Add" loading={createDraftInvoiceMutation.isPending} onClick={createDraftInvoice}>
            {t('invoices:overview.emptyState.createInvoice')}
          </Button>
        ) : (
          <Button onClick={() => completeAccountDialog.open()}>{t('invoices:overview.emptyState.completeAccount')}</Button>
        )}
      </Card.Root>

      {completeAccountDialog.isOpen && (
        <CompleteAccountDialog onClose={() => completeAccountDialog.close(false)} onComplete={() => completeAccountDialog.close(true)} />
      )}
    </>
  );
};
